/**
 * promptThemes — shared analysis of what people ask Prompt-Brush to draw.
 * Single source of truth for the About panel chart and the admin social exports.
 */

export interface PromptTheme {
  label: string;
  count: number;
}

// `label` is the chart label (plural); `word` is the singular base used in the
// write-a-prompt notice ("the word \"cat\"" / "83 cat-prompts").
const THEMES: { label: string; word: string; re: RegExp }[] = [
  { label: 'cats', word: 'cat', re: /\b(cats?|kittens?|kitty|kitties)\b/i },
  { label: 'dogs', word: 'dog', re: /\b(dogs?|pupp(?:y|ies)|pups?)\b/i },
  { label: 'love', word: 'love', re: /\blove(s|d|ly)?\b/i },
  { label: 'the sun', word: 'sun', re: /\bsun(s|ny|shine)?\b/i },
  { label: 'the moon', word: 'moon', re: /\bmoons?\b/i },
  { label: 'hearts', word: 'heart', re: /\bhearts?\b/i },
  { label: 'flowers', word: 'flower', re: /\bflowers?\b/i },
  { label: 'coffee', word: 'coffee', re: /\bcoffee\b/i },
  { label: 'birds', word: 'bird', re: /\bbirds?\b/i },
  { label: 'trees', word: 'tree', re: /\btrees?\b/i },
  { label: 'the sea', word: 'sea', re: /\b(ocean|sea|waves?)\b/i },
  { label: 'robots', word: 'robot', re: /\brobots?\b/i },
];

/** Count theme occurrences across prompt texts; returns the top `top`, biggest first. */
export function computePromptThemes(
  prompts: { prompt?: string; text?: string }[],
  top = 6
): PromptTheme[] {
  return THEMES.map((t) => ({
    label: t.label,
    count: prompts.reduce(
      (n, p) => n + (t.re.test(String(p.prompt || p.text || '')) ? 1 : 0),
      0
    ),
  }))
    .filter((t) => t.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, top);
}

const wordCount = (s?: string) =>
  String(s || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

/** Distribution of prompts by word count — for the "how long is a prompt" histogram. */
export function computePromptLengths(prompts: { prompt?: string; text?: string }[]): PromptTheme[] {
  const buckets: { label: string; test: (n: number) => boolean }[] = [
    { label: '1 word', test: (n) => n === 1 },
    { label: '2–3', test: (n) => n >= 2 && n <= 3 },
    { label: '4–6', test: (n) => n >= 4 && n <= 6 },
    { label: '7–10', test: (n) => n >= 7 && n <= 10 },
    { label: '11+', test: (n) => n >= 11 },
  ];
  const counts = prompts.map((p) => wordCount(p.prompt || p.text)).filter((n) => n > 0);
  return buckets
    .map((b) => ({ label: b.label, count: counts.filter(b.test).length }))
    .filter((b) => b.count > 0);
}

/** Artists ranked by how many drawings they've made — for the "most drawings" bars. */
export function computeTopArtists(
  artworks: { artistName?: string | null }[],
  top = 6
): PromptTheme[] {
  // "f.decombat" and "F.decombat" are one person: count case-insensitively and
  // show the spelling they use most.
  const counts = new Map<string, { n: number; spellings: Map<string, number> }>();
  for (const a of artworks) {
    const name = String(a.artistName || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const cur = counts.get(key) || { n: 0, spellings: new Map<string, number>() };
    cur.n += 1;
    cur.spellings.set(name, (cur.spellings.get(name) || 0) + 1);
    counts.set(key, cur);
  }
  return [...counts.values()]
    .map((c) => ({
      label: [...c.spellings.entries()].sort((x, y) => y[1] - x[1])[0][0],
      count: c.n,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, top);
}

/**
 * If a prompt contains a common theme word that already has at least `minCount`
 * prompts, return that word + its current total — for the "you're in good company"
 * notice. Picks the most-common matching word; returns null if none qualify.
 */
export function findOverusedTheme(
  text: string,
  prompts: { prompt?: string; text?: string }[],
  minCount = 10
): { word: string; count: number } | null {
  const t = String(text || '');
  let best: { word: string; count: number } | null = null;
  for (const theme of THEMES) {
    if (!theme.re.test(t)) continue;
    const count = prompts.reduce(
      (n, p) => n + (theme.re.test(String(p.prompt || p.text || '')) ? 1 : 0),
      0
    );
    if (count >= minCount && (!best || count > best.count)) best = { word: theme.word, count };
  }
  return best;
}

// ---- Timestamps: the API serialises created/completed as ISO strings ----
const DAY_MS = 86_400_000;
function toMs(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = Date.parse(String(v ?? ''));
  return Number.isFinite(n) ? n : NaN;
}

type PromptRow = {
  prompt?: string;
  status?: string;
  createdAt?: unknown;
  completedAt?: unknown;
  artworkId?: string;
};
type ArtworkRow = {
  artistName?: string | null;
  createdAt?: unknown;
  promptId?: string | null;
  promptText?: string | null;
  heartCount?: number | null;
};
type PromptRowWithId = PromptRow & { id?: string };

/**
 * The prompts that have an approved drawing, by prompt id. The prompt's own
 * status can lag (a second, rejected drawing used to reset it to pending), so
 * the drawings are the truth wherever they are available.
 */
function drawnPromptIds(artworks: ArtworkRow[]): Set<string> {
  const s = new Set<string>();
  for (const a of artworks) if (a.promptId) s.add(String(a.promptId));
  return s;
}
const isDrawn = (p: PromptRowWithId, drawn: Set<string>) =>
  p.status === 'completed' || (!!p.id && drawn.has(String(p.id)));

/** Drawn vs. still-waiting — for the completion donut. */
export function computeDrawnVsWaiting(prompts: PromptRowWithId[], artworks: ArtworkRow[] = []): PromptTheme[] {
  const drawnIds = drawnPromptIds(artworks);
  let drawn = 0;
  let waiting = 0;
  for (const p of prompts) {
    if (isDrawn(p, drawnIds)) drawn++;
    else waiting++;
  }
  return [
    { label: 'Drawn', count: drawn },
    { label: 'Still waiting', count: waiting },
  ].filter((t) => t.count > 0);
}

/** How long a completed prompt waited to be drawn — buckets + the median in days. */
export function computeTimeToDraw(prompts: PromptRowWithId[], artworks: ArtworkRow[] = []): {
  buckets: PromptTheme[];
  medianDays: number | null;
} {
  const firstDrawing = new Map<string, number>();
  for (const a of artworks) {
    const t = toMs(a.createdAt);
    if (!a.promptId || !Number.isFinite(t)) continue;
    const k = String(a.promptId);
    firstDrawing.set(k, Math.min(firstDrawing.get(k) ?? Infinity, t));
  }
  const days: number[] = [];
  for (const p of prompts) {
    const a = toMs(p.createdAt);
    let b = toMs(p.completedAt);
    if (!Number.isFinite(b) && p.id && firstDrawing.has(String(p.id))) b = firstDrawing.get(String(p.id))!;
    if (p.status !== 'completed' && !Number.isFinite(b)) continue;
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) continue;
    days.push((b - a) / DAY_MS);
  }
  const buckets: { label: string; test: (d: number) => boolean }[] = [
    { label: 'Same day', test: (d) => d < 1 },
    { label: '1–2 days', test: (d) => d >= 1 && d < 3 },
    { label: '3–6 days', test: (d) => d >= 3 && d < 7 },
    { label: '1–4 weeks', test: (d) => d >= 7 && d < 28 },
    { label: 'A month+', test: (d) => d >= 28 },
  ];
  const out = buckets
    .map((b) => ({ label: b.label, count: days.filter(b.test).length }))
    .filter((b) => b.count > 0);
  let medianDays: number | null = null;
  if (days.length) {
    const s = [...days].sort((x, y) => x - y);
    medianDays = Math.round(s[Math.floor(s.length / 2)]);
  }
  return { buckets: out, medianDays };
}

// Common words to ignore when ranking what people actually write about.
const STOP_WORDS = new Set([
  'the','a','an','of','and','to','in','on','at','for','with','is','it','its','my','your','that','this',
  'as','by','or','from','into','over','under','out','up','off','about','was','are','be','been','being',
  'he','she','they','we','you','i','me','his','her','their','our','him','them','who','what','which',
  'but','if','so','than','then','there','here','too','very','just','not','no','yes','can','will',
  'draw','drawing','drawn','picture','please','make','made','one','some','any','all','more','most',
  'like','two','three','four','five','has','have','having','had','do','does','doing','did','get','gets',
  'getting','got','go','goes','going','went','want','wants','would','could','should','also','only','each',
  'other','another','much','many','lot','lots','really','something','someone','thing','things','while',
  'when','where','how','why','because','through','without','within','before','after','during','again',
  'both','same','such','let','lets','via','per',
  // the Spanish and French filler our prompts arrive with
  'de','la','el','los','las','un','una','unos','unas','y','en','con','que','por','para','del','al','es',
  'le','les','et','des','du','au','aux','est','dans','sur','avec',
]);

/** The words people actually use most (one count per prompt, stop-words removed). */
export function computeTopWords(prompts: PromptRow[], top = 8): PromptTheme[] {
  const counts = new Map<string, number>();
  for (const p of prompts) {
    const words = String(p.prompt || '').toLowerCase().match(/[\p{L}\p{M}']+/gu) || [];
    const seen = new Set<string>();
    for (let w of words) {
      w = w.replace(/^'+|'+$/g, '');
      if (w.length < 3 || STOP_WORDS.has(w) || seen.has(w)) continue;
      seen.add(w);
      counts.set(w, (counts.get(w) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, top);
}

/** The shape of the community: how many drawings each artist has made. */
export function computeArtistDistribution(artworks: ArtworkRow[]): PromptTheme[] {
  const counts = new Map<string, number>();
  for (const a of artworks) {
    const n = String(a.artistName || '').trim().toLowerCase();
    if (!n) continue;
    counts.set(n, (counts.get(n) || 0) + 1);
  }
  const vals = [...counts.values()];
  const buckets: { label: string; test: (c: number) => boolean }[] = [
    { label: '1', test: (c) => c === 1 },
    { label: '2–5', test: (c) => c >= 2 && c <= 5 },
    { label: '6–20', test: (c) => c >= 6 && c <= 20 },
    { label: '21+', test: (c) => c >= 21 },
  ];
  return buckets
    .map((b) => ({ label: b.label, count: vals.filter(b.test).length }))
    .filter((b) => b.count > 0);
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Prompts asked per month (the most recent `months`) — growth over time. */
export function computePromptsByMonth(prompts: PromptRow[], months = 6): PromptTheme[] {
  const counts = new Map<string, number>();
  for (const p of prompts) {
    const t = toMs(p.createdAt);
    if (!Number.isFinite(t)) continue;
    const d = new Date(t);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()).padStart(2, '0')}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.keys()]
    .sort()
    .slice(-months)
    .map((k) => {
      const [, m] = k.split('-');
      return { label: MONTH_NAMES[Number(m)] || k, count: counts.get(k)! };
    });
}

/** When drawings get made, by day of week — when the community is alive. */
export function computeBusiestDay(artworks: ArtworkRow[]): PromptTheme[] {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const counts = new Array(7).fill(0);
  for (const a of artworks) {
    const t = toMs(a.createdAt);
    if (!Number.isFinite(t)) continue;
    counts[new Date(t).getDay()]++;
  }
  return names.map((label, i) => ({ label, count: counts[i] }));
}

/** Small editorial one-liners for the bottom of the stats panel. */
export function computeFunFacts(
  prompts: PromptRow[],
  artworks: ArtworkRow[],
  now = Date.now()
): { drawingsThisWeek: number; promptsToday: number; avgWords: number; longest: string; shortest: string } {
  let drawingsThisWeek = 0;
  for (const a of artworks) {
    const t = toMs(a.createdAt);
    if (Number.isFinite(t) && now - t < 7 * DAY_MS) drawingsThisWeek++;
  }
  let promptsToday = 0;
  for (const p of prompts) {
    const t = toMs(p.createdAt);
    if (Number.isFinite(t) && now - t < DAY_MS) promptsToday++;
  }
  const withWords = prompts
    .map((p) => ({ text: String(p.prompt || '').trim(), n: wordCount(p.prompt) }))
    .filter((x) => x.n > 0);
  let longest = '';
  let shortest = '';
  if (withWords.length) {
    longest = withWords.reduce((a, b) => (b.n > a.n ? b : a)).text;
    shortest = withWords.reduce((a, b) => (b.n < a.n ? b : a)).text;
  }
  const avgWords = withWords.length
    ? Math.round(withWords.reduce((s, x) => s + x.n, 0) / withWords.length)
    : 0;
  return { drawingsThisWeek, promptsToday, avgWords, longest, shortest };
}

// ---- Newer readings of the same data ----

/** Monday-based week bin, so "different weeks" means calendar weeks, not any 7-day span. */
const WEEK_MS = 7 * DAY_MS;
const WEEK_EPOCH = Date.UTC(2025, 0, 6); // a Monday
const weekOf = (t: number) => Math.floor((t - WEEK_EPOCH) / WEEK_MS);

/**
 * How many different weeks each artist has drawn in — the shape of coming back.
 * `regulars` is the number of people who have drawn in four or more different weeks.
 */
export function computeReturning(artworks: ArtworkRow[]): { buckets: PromptTheme[]; regulars: number } {
  const weeks = new Map<string, Set<number>>();
  for (const a of artworks) {
    const name = String(a.artistName || '').trim().toLowerCase();
    const t = toMs(a.createdAt);
    if (!name || !Number.isFinite(t)) continue;
    (weeks.get(name) || weeks.set(name, new Set()).get(name)!).add(weekOf(t));
  }
  const vals = [...weeks.values()].map((s) => s.size);
  const buckets: { label: string; test: (n: number) => boolean }[] = [
    { label: '1 week', test: (n) => n === 1 },
    { label: '2–3', test: (n) => n >= 2 && n <= 3 },
    { label: '4–9', test: (n) => n >= 4 && n <= 9 },
    { label: '10+', test: (n) => n >= 10 },
  ];
  return {
    buckets: buckets.map((b) => ({ label: b.label, count: vals.filter(b.test).length })).filter((b) => b.count > 0),
    regulars: vals.filter((n) => n >= 4).length,
  };
}

/** The marks people leave on drawings: how many, how widely, and the most-marked drawing. */
export function computeMarks(artworks: ArtworkRow[]): {
  total: number;
  marked: number;
  share: number;
  top: { prompt: string; artist: string; count: number } | null;
} {
  let total = 0;
  let marked = 0;
  let top: { prompt: string; artist: string; count: number } | null = null;
  for (const a of artworks) {
    const n = Number(a.heartCount) || 0;
    total += n;
    if (n > 0) marked++;
    if (n > 0 && (!top || n > top.count)) {
      top = { prompt: String(a.promptText || '').trim(), artist: String(a.artistName || '').trim(), count: n };
    }
  }
  return { total, marked, share: artworks.length ? marked / artworks.length : 0, top };
}

/** The queue: how long the prompts still waiting have waited, in days. */
export function computeQueueAge(
  prompts: PromptRowWithId[],
  artworks: ArtworkRow[] = [],
  now = Date.now()
): { waiting: number; medianDays: number | null; oldestDays: number | null } {
  const drawnIds = drawnPromptIds(artworks);
  const ages: number[] = [];
  for (const p of prompts) {
    if (isDrawn(p, drawnIds)) continue;
    const t = toMs(p.createdAt);
    if (Number.isFinite(t)) ages.push(Math.max(0, now - t) / DAY_MS);
  }
  if (!ages.length) return { waiting: 0, medianDays: null, oldestDays: null };
  const s = [...ages].sort((x, y) => x - y);
  return { waiting: ages.length, medianDays: Math.round(s[Math.floor(s.length / 2)]), oldestDays: Math.round(s[s.length - 1]) };
}
